import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API, toFullUrl } from '../config/api';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Play, Pause, ExternalLink, ArrowLeft, Music, Users, Film, ChevronLeft, ChevronRight, X, Download } from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';
import { Button } from '../components/ui/button';

const SOCIAL_LABELS = {
  spotify: 'Spotify',
  apple_music: 'Apple Music',
  soundcloud: 'SoundCloud',
  youtube_music: 'YouTube Music',
  tidal: 'Tidal',
  amazon_music: 'Amazon Music',
  deezer: 'Deezer',
  audiomack: 'Audiomack',
  bandcamp: 'Bandcamp',
  pandora: 'Pandora',
  instagram: 'Instagram',
  twitter: 'Twitter / X',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  website: 'Website',
};

// Music streaming platforms (rendered as a prominent "Listen On" strip)
const STREAMING_PLATFORMS = ['spotify', 'apple_music', 'soundcloud', 'youtube_music', 'tidal', 'amazon_music', 'deezer', 'audiomack', 'bandcamp', 'pandora'];

/** Parse a YouTube or Vimeo URL into an embeddable iframe src. */
function videoEmbedUrl(raw) {
  if (!raw) return null;
  const url = String(raw).trim();
  // YouTube short or watch
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/i);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  // Vimeo
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

export default function ArtistPage() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const { currentTrack, isPlaying, playTrack, pause, clearQueue, addToQueue } = usePlayer();
  const heroRef = useRef(null);

  useEffect(() => {
    const fetchArtist = async () => {
      try {
        const res = await axios.get(`${API}/featured-artists/${slug}`);
        setArtist(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Artist not found' : 'Failed to load artist');
      } finally {
        setLoading(false);
      }
    };
    fetchArtist();
  }, [slug]);

  // Parallax scroll on hero
  useEffect(() => {
    if (!heroRef.current) return;
    const el = heroRef.current;
    const onScroll = () => {
      const y = window.scrollY;
      el.style.transform = `translate3d(0, ${y * 0.3}px, 0)`;
      el.style.opacity = Math.max(0, 1 - y / 600);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [artist]);

  // Lightbox keyboard nav
  useEffect(() => {
    if (lightboxIdx === null || !artist?.gallery?.length) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      if (e.key === 'ArrowRight') setLightboxIdx((i) => (i + 1) % artist.gallery.length);
      if (e.key === 'ArrowLeft') setLightboxIdx((i) => (i - 1 + artist.gallery.length) % artist.gallery.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, artist]);

  const playerTracks = useMemo(() => {
    if (!artist?.tracks) return [];
    return artist.tracks.map((t) => ({
      id: t.id,
      title: t.title,
      artist: artist.name,
      preview_url: t.audio_url,
      audio_url: t.audio_url,
      cover_image: t.cover_url || artist.photo || '',
      price: 0,
      preview_mode: t.preview_mode || artist.preview_mode || 'full',
      unlock_price: t.unlock_price || 0,
      unlock_item_type: 'featured_track',
    }));
  }, [artist]);

  const playTrackAt = (track, index) => {
    if (!playerTracks.length) return;
    if (currentTrack?.id === track.id && isPlaying) {
      pause();
      return;
    }
    clearQueue();
    // Add the rest of the artist tracks to the queue
    playerTracks.slice(index + 1).forEach((t) => addToQueue(t));
    playTrack(playerTracks[index]);
  };

  const playAll = () => {
    if (!playerTracks.length) return;
    clearQueue();
    playerTracks.slice(1).forEach((t) => addToQueue(t));
    playTrack(playerTracks[0]);
  };

  const videoEmbeds = (artist?.videos || []).map(videoEmbedUrl).filter(Boolean);
  const showDownloads = artist?.show_free_downloads !== false;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#DAA520] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !artist) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-4 px-4">
        <Users className="w-12 h-12 text-[#333]" />
        <h1 className="font-cabinet text-2xl font-black uppercase">{error || 'Artist Not Found'}</h1>
        <Link to="/">
          <Button variant="outline" className="rounded-none border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> BACK TO HOME
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="artist-page">
      <Helmet>
        <title>{`${artist.name} | Featured Artist`}</title>
      </Helmet>

      {/* Cinematic Hero — full-bleed banner with parallax */}
      <div className="relative h-[68vh] sm:h-[80vh] min-h-[460px] overflow-hidden">
        <div ref={heroRef} className="absolute inset-0 will-change-transform">
          {artist.cover_image ? (
            <img src={toFullUrl(artist.cover_image)} alt="" className="w-full h-full object-cover" />
          ) : artist.photo ? (
            <img src={toFullUrl(artist.photo)} alt="" className="w-full h-full object-cover scale-110 blur-md opacity-60" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#1A1A1A] via-[#0A0A0A] to-[#000]" />
          )}
        </div>
        {/* Grain + multi-stop dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        <div className="absolute inset-0 mix-blend-overlay opacity-30"
          style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'120\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'2\' stitchTiles=\'stitch\'/></filter><rect width=\'120\' height=\'120\' filter=\'url(%23n)\' opacity=\'0.45\'/></svg>")' }} />

        {/* Back arrow */}
        <Link to="/" className="absolute top-5 left-5 z-20 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white transition-colors backdrop-blur-md bg-black/30 px-3 py-2 border border-white/10">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>

        {/* Hero text */}
        <div className="absolute inset-x-0 bottom-0 z-10 px-4 sm:px-12 pb-10 sm:pb-16">
          <div className="max-w-6xl mx-auto">
            <p className="text-[10px] sm:text-xs tracking-[0.4em] text-[#FF3B30] mb-3 animate-fade-up" style={{ animationDelay: '0.1s' }}>FEATURED ARTIST</p>
            <h1 className="font-cabinet text-5xl sm:text-7xl md:text-8xl font-black uppercase tracking-tighter leading-[0.85] animate-fade-up"
              style={{ animationDelay: '0.2s' }} data-testid="artist-name">
              {artist.name}
            </h1>
            {/* Inline play-all + socials */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-6 sm:mt-8 animate-fade-up" style={{ animationDelay: '0.35s' }}>
              {playerTracks.length > 0 && (
                <button onClick={playAll}
                  className="group flex items-center gap-3 px-5 py-3 bg-[#DAA520] hover:bg-white text-black font-black uppercase text-sm tracking-wider transition-all hover:shadow-[0_0_20px_rgba(218,165,32,0.4)]"
                  data-testid="play-all">
                  <Play className="w-4 h-4 fill-current" />
                  PLAY ALL
                  <span className="text-[10px] font-mono opacity-60">·{playerTracks.length}</span>
                </button>
              )}
              {artist.links && Object.entries(artist.links).map(([platform, url]) => {
                if (!url) return null;
                return (
                  <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] sm:text-xs text-white/70 hover:text-[#DAA520] transition-colors flex items-center gap-1 uppercase font-bold tracking-wider"
                    data-testid={`artist-link-${platform}`}>
                    <ExternalLink className="w-3 h-3" />
                    {SOCIAL_LABELS[platform] || platform}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Photo + Bio block */}
      <div className="max-w-6xl mx-auto px-4 sm:px-12 -mt-20 sm:-mt-28 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 sm:gap-10 items-start">
          {/* Avatar */}
          <div className="w-40 h-40 sm:w-48 sm:h-48 md:w-full md:aspect-square border-4 border-[#0A0A0A] bg-[#141414] overflow-hidden shadow-2xl">
            {artist.photo ? (
              <img src={toFullUrl(artist.photo)} alt={artist.name} className="w-full h-full object-cover" data-testid="artist-photo" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[#1A1A1A]">
                <Users className="w-14 h-14 text-[#333]" />
              </div>
            )}
          </div>
          {/* Bio */}
          {artist.bio && (
            <div className="pt-4 md:pt-12">
              <p className="overline text-[#FF3B30] text-[10px] tracking-[0.4em] mb-3">BIO</p>
              <p className="text-base sm:text-lg text-[#D4D4D4] leading-relaxed whitespace-pre-line max-w-2xl" data-testid="artist-bio">
                {artist.bio}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* LISTEN ON — music streaming platforms strip */}
      {artist.links && STREAMING_PLATFORMS.some(p => artist.links[p]) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-12 pt-12 sm:pt-16" data-testid="listen-on-strip">
          <p className="overline text-[#DAA520] text-[10px] tracking-[0.4em] mb-4">LISTEN ON</p>
          <div className="flex flex-wrap items-center gap-3">
            {STREAMING_PLATFORMS.map((platform) => {
              const url = artist.links[platform];
              if (!url) return null;
              return (
                <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-4 py-2.5 bg-[#141414] border border-[#262626] hover:border-[#DAA520] hover:bg-[#DAA520]/10 transition-all text-white/80 hover:text-white text-xs font-bold uppercase tracking-wider"
                  data-testid={`streaming-link-${platform}`}>
                  <span className="text-[#DAA520]">▸</span>
                  {SOCIAL_LABELS[platform] || platform}
                  <ExternalLink className="w-3 h-3 opacity-40 group-hover:opacity-100 transition-opacity" />
                </a>
              );
            })}
          </div>
        </section>
      )}

      {/* Tracks */}
      {playerTracks.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-12 pt-16 sm:pt-24">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="overline text-[#FF3B30] text-[10px] tracking-[0.4em] mb-2">TRACKS</p>
              <h2 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter">
                {playerTracks.length} SONG{playerTracks.length !== 1 ? 'S' : ''}
              </h2>
            </div>
            {artist.preview_mode === 'snippet' && (
              <span className="text-[10px] tracking-[0.3em] text-[#DAA520] uppercase font-bold px-2 py-1 border border-[#DAA520]/30">
                30s Preview
              </span>
            )}
          </div>
          <div className="space-y-px bg-[#141414] border border-[#262626]">
            {artist.tracks.map((track, idx) => {
              const isActive = currentTrack?.id === track.id;
              return (
                <button key={track.id} onClick={() => playTrackAt(track, idx)}
                  className={`group w-full flex items-center gap-4 p-3 sm:p-4 transition-all text-left border-l-2 ${isActive ? 'bg-[#DAA520]/10 border-l-[#DAA520]' : 'border-l-transparent hover:bg-[#1A1A1A] hover:border-l-[#FF3B30]'}`}
                  data-testid={`artist-track-${track.id}`}>
                  <span className="text-xs text-[#666] font-mono w-6">{String(idx + 1).padStart(2, '0')}</span>
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'text-[#DAA520]' : 'text-white group-hover:text-[#FF3B30]'}`}>
                    {isActive && isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current" />}
                  </div>
                  {track.cover_url ? (
                    <img src={toFullUrl(track.cover_url)} alt="" className="w-12 h-12 object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 bg-[#262626] flex items-center justify-center flex-shrink-0">
                      <Music className="w-4 h-4 text-[#666]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold truncate text-sm sm:text-base ${isActive ? 'text-[#DAA520]' : 'text-white'}`}>{track.title}</p>
                    <p className="text-[10px] sm:text-xs text-[#666] uppercase tracking-wider">{artist.name}</p>
                  </div>
                  {showDownloads && track.audio_url && (
                    <a href={toFullUrl(track.audio_url)} download
                      onClick={(e) => e.stopPropagation()}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-[#A3A3A3] hover:text-[#22C55E] p-2"
                      title="Download"
                      data-testid={`dl-track-${track.id}`}>
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Videos (up to 3) */}
      {videoEmbeds.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-12 pt-16 sm:pt-24" data-testid="artist-videos">
          <p className="overline text-[#FF3B30] text-[10px] tracking-[0.4em] mb-2">VIDEOS</p>
          <h2 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-6">
            WATCH
          </h2>
          <div className={`grid gap-4 ${videoEmbeds.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            {videoEmbeds.map((src, i) => (
              <div key={i} className="relative aspect-video bg-black border border-[#262626] overflow-hidden" data-testid={`video-embed-${i}`}>
                <iframe
                  src={src}
                  title={`${artist.name} video ${i + 1}`}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Gallery — masonry-ish with lightbox */}
      {Array.isArray(artist.gallery) && artist.gallery.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-12 pt-16 sm:pt-24" data-testid="artist-gallery">
          <p className="overline text-[#FF3B30] text-[10px] tracking-[0.4em] mb-2">GALLERY</p>
          <h2 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-6">
            SHOTS
          </h2>
          <div className={`grid gap-3 ${artist.gallery.length === 1 ? 'grid-cols-1' : artist.gallery.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : artist.gallery.length <= 4 ? 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'}`}>
            {artist.gallery.map((url, idx) => (
              <button key={idx} onClick={() => setLightboxIdx(idx)}
                className="block aspect-square bg-[#141414] border border-[#262626] overflow-hidden group relative"
                data-testid={`gallery-photo-${idx}`}>
                <img src={toFullUrl(url)} alt={`${artist.name} ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Footer back link */}
      <div className="max-w-6xl mx-auto px-4 sm:px-12 pt-20 pb-16">
        <Link to="/" className="text-xs uppercase tracking-[0.3em] text-[#A3A3A3] hover:text-[#FF3B30] transition-colors flex items-center gap-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
        </Link>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && artist.gallery?.[lightboxIdx] && (
        <div className="fixed inset-0 bg-black/95 z-[200] flex items-center justify-center p-4" onClick={() => setLightboxIdx(null)} data-testid="lightbox">
          <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white" data-testid="lightbox-close">
            <X className="w-6 h-6" />
          </button>
          {artist.gallery.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + artist.gallery.length) % artist.gallery.length); }}
                className="absolute left-4 sm:left-8 w-12 h-12 flex items-center justify-center text-white/70 hover:text-white bg-black/40 backdrop-blur-sm" data-testid="lightbox-prev">
                <ChevronLeft className="w-7 h-7" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % artist.gallery.length); }}
                className="absolute right-4 sm:right-8 w-12 h-12 flex items-center justify-center text-white/70 hover:text-white bg-black/40 backdrop-blur-sm" data-testid="lightbox-next">
                <ChevronRight className="w-7 h-7" />
              </button>
            </>
          )}
          <img src={toFullUrl(artist.gallery[lightboxIdx])} alt=""
            className="max-h-[88vh] max-w-[92vw] object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()} />
          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.3em] text-white/50 font-mono">
            {String(lightboxIdx + 1).padStart(2, '0')} / {String(artist.gallery.length).padStart(2, '0')}
          </span>
        </div>
      )}
    </div>
  );
}
